import {
  Checkbox,
  Group,
  Button,
  Text,
  Stack,
  SimpleGrid,
  Card,
  Badge,
  Divider,
} from '@mantine/core';
import { plants } from '../data/plants';

interface SettingsViewProps {
  isSelected: (name: string) => boolean;
  toggle: (name: string) => void;
  selectAll: () => void;
  selectNone: () => void;
  selectCategory: (category: string) => void;
  selectedCount: number;
}

const CATEGORY_CONFIG = [
  { value: 'vegetable', label: 'Vegetables', color: 'blue' },
  { value: 'herb', label: 'Herbs', color: 'teal' },
  { value: 'fruit', label: 'Fruit', color: 'grape' },
  { value: 'flower', label: 'Flowers', color: 'pink' },
] as const;

export function SettingsView({
  isSelected,
  toggle,
  selectAll,
  selectNone,
  selectCategory,
  selectedCount,
}: SettingsViewProps) {
  const grouped = CATEGORY_CONFIG.map(({ value, label, color }) => ({
    category: value,
    label,
    color,
    items: plants.filter((p) => p.category === value).sort((a, b) => a.name.localeCompare(b.name)),
  }));

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Select which plants to show across all views. Saved automatically.
        </Text>
        <Badge variant="light" size="lg">
          {selectedCount} / {plants.length} selected
        </Badge>
      </Group>

      <Group gap="xs">
        <Button size="xs" variant="light" onClick={selectAll}>Select All</Button>
        <Button size="xs" variant="light" color="gray" onClick={selectNone}>Deselect All</Button>
        {CATEGORY_CONFIG.map(({ value, label, color }) => (
          <Button
            key={value}
            size="xs"
            variant="light"
            color={color}
            onClick={() => selectCategory(value)}
          >
            Only {label}
          </Button>
        ))}
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        {grouped.map(({ category, label, color, items }) => (
          <Card key={category} shadow="sm" padding="md" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text fw={600}>{label}</Text>
              <Badge color={color} variant="light" size="sm">
                {items.filter((p) => isSelected(p.name)).length}/{items.length}
              </Badge>
            </Group>
            <Divider mb="sm" />
            <Stack gap={6}>
              {items.map((plant) => (
                <Checkbox
                  key={plant.name}
                  label={plant.name}
                  size="sm"
                  checked={isSelected(plant.name)}
                  onChange={() => toggle(plant.name)}
                />
              ))}
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
